import {
  useCreateStandardsChatApi,
  useStandardsChatWithAuthHeaders,
} from '@/api/standards';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { paths } from '@/paths';
import { type StandardsChatContext } from '@ditto/shared';
import { FileSearch } from 'lucide-react';
import { Fragment, type FC } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Chat } from '../Chat/components/Chat';

// Landing page for standards discovery
export const StandardsPage: FC = () => {
  const navigate = useNavigate();
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const { mutate: createChat, isPending } = useCreateStandardsChatApi();

  const handleStartChat = () => {
    createChat(undefined, {
      onSuccess: data => {
        navigate(`/workspace/${workspaceUname}/${paths.standards}/${data.id}`);
      },
    });
  };

  return (
    <FlexCol className="h-full w-full items-center justify-center bg-background p-8">
      <FlexCol className="max-w-2xl items-center text-center gap-6">
        <div className="rounded-full bg-primary/10 p-4">
          <FileSearch className="h-12 w-12 text-primary" />
        </div>
        <Text variant="header1">Standards Discovery</Text>
        <Text variant="body-lg" className="text-muted-foreground">
          Find applicable regulatory standards for your medical device. Our AI
          assistant will help you identify relevant standards by asking about
          your device characteristics.
        </Text>
        <Button size="lg" onClick={handleStartChat} disabled={isPending}>
          {isPending ? 'Starting...' : 'Start Discovery'}
        </Button>
      </FlexCol>
    </FlexCol>
  );
};

// Chat page for standards discovery
export const StandardsChatPage: FC = () => {
  const { chat_id } = useParams<{ chat_id: string }>();
  const navigate = useNavigate();

  const getChatContext = (): StandardsChatContext => {
    return {
      id: chat_id ?? '',
    };
  };

  const { messages, sendMessage, status, regenerate, error, clearError, stop } =
    useStandardsChatWithAuthHeaders(getChatContext);

  const lastMessage = messages[messages.length - 1];

  const isThinking =
    status === 'submitted' ||
    ((status === 'streaming' || status === 'ready') &&
      messages.length > 0 &&
      lastMessage?.parts.length <= 1);

  const isDisabled = status !== 'ready';

  const showStop = status === 'streaming' || status === 'submitted';

  const handleClose = () => {
    navigate(-1);
  };

  const handleErrorRegenerate = () => {
    clearError();
    regenerate();
  };

  return (
    <Chat messages={messages} chatData={null} sendMessage={sendMessage}>
      <Chat.Header onClose={handleClose} />
      <FlexRow w="full" className="h-[calc(100%-65px)]">
        <Chat.Layout>
          <Chat.Content>
            {messages.length === 0 && (
              <FlexCol className="items-center justify-center h-full text-center px-8">
                <Text variant="header2" className="mb-2">
                  Standards Discovery Assistant
                </Text>
                <Text variant="body" className="text-muted-foreground max-w-md">
                  Tell me about your medical device and I will help you identify
                  applicable regulatory standards. Start by describing your
                  device.
                </Text>
              </FlexCol>
            )}
            {messages.map((message, idx) => (
              <Fragment key={message.id}>
                {message.role === 'assistant' && (
                  <Chat.AiReasoningBlock
                    key={`${message.id}-${idx}`}
                    message={message}
                    isThinking={isThinking}
                    isLatestMessage={idx === messages.length - 1}
                  />
                )}
                <Chat.Message
                  key={message.id || idx}
                  message={message}
                  messageIndex={idx}
                  isLastMessage={idx === messages.length - 1}
                  onFeedback={() => {}}
                />
              </Fragment>
            ))}
            {isThinking && lastMessage?.role === 'user' && (
              <Chat.AiReasoningBlock
                message={{
                  id: 'thinking',
                  role: 'assistant',
                  parts: [],
                }}
                isThinking={true}
                isLatestMessage
              />
            )}
            {error && (
              <Chat.ErrorMessage onRegenerate={handleErrorRegenerate} />
            )}
          </Chat.Content>
          <Chat.ScrollToBottomButton />
          <Chat.Input
            type="chat"
            onSubmit={sendMessage}
            disabled={isDisabled}
            onStop={showStop ? stop : undefined}
            placeholder="Describe your medical device..."
            hideRegionSelector
          />
        </Chat.Layout>
      </FlexRow>
    </Chat>
  );
};
